/**
 * Standalone page — embed: /learn-more/quickbooks-desktop
 */
import React from "react";
import {
    LearnMoreShell,
    WhatIsForaConnectorSection,
    ForaSignupSection,
    QuickBooksTrademarkSection,
    LearnMoreFooter,
} from "./shared";

export default function LearnMoreQuickBooksDesktop() {
    return (
        <LearnMoreShell
            title="QuickBooks Desktop & Fora Connector"
            subtitle={
                <>
                    Connect <strong>QuickBooks Desktop</strong> to the <strong>Fora Mobile Commerce Platform</strong>{" "}
                    using the Fora Connector web app and desktop helper on Windows.
                </>
            }
        >
            <div className="space-y-6 text-gray-700">
                <WhatIsForaConnectorSection />
                <section className="rounded-lg border border-gray-100 border-l-4 border-l-indigo-700 bg-white shadow-sm p-6 sm:p-8">
                    <p className="text-xs font-bold uppercase tracking-wide text-indigo-800 mb-1">Platform</p>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Overview</h2>
                    <p className="leading-relaxed mb-4">
                        Use this option when you run <strong>QuickBooks Desktop</strong> (for example QuickBooks
                        Enterprise or other supported desktop editions) on a Windows PC. Data exchange uses the{" "}
                        <strong>Fora Connector desktop application</strong> and Intuit&apos;s desktop integration stack
                        alongside your open company file.
                    </p>
                    <ul className="list-disc pl-5 space-y-2 leading-relaxed">
                        <li>
                            <strong>Before setup:</strong> Keep customers and products active in QuickBooks Desktop, make
                            sure customer payment terms are not blank, and prepare product custom fields for values you
                            want in Fora (department, category, style, color, size, minimum quantity, and similar fields).
                        </li>
                        <li>
                            <strong>Product sync flag:</strong> Mark products intended for Fora with a custom field named{" "}
                            <strong>Fora</strong> set to <strong>Yes</strong>.
                        </li>
                        <li>
                            <strong>Desktop app &amp; prerequisites:</strong> The connector checks that the Fora desktop
                            helper is running, required QuickBooks components (such as QuickBooks Foundation Class, QBFC) are available, and
                            QuickBooks Desktop is running with the correct company file when needed.
                        </li>
                        <li>
                            <strong>QuickBooks access mode:</strong> For first-time authorization, sign in to QuickBooks
                            Desktop as Administrator, switch to single-user mode, and grant the Connector access in
                            integrated application preferences when prompted.
                        </li>
                        <li>
                            <strong>Connect:</strong> Complete the desktop connection steps in the connector so your
                            QuickBooks Desktop company is paired with your Fora configuration.
                        </li>
                        <li>
                            <strong>Mapping:</strong> Map fields between QuickBooks Desktop and Fora, using
                            desktop-specific field sets where applicable. Searching a sample customer/item/order (or a
                            prepared dummy record with values in each field) helps verify mapping quickly.
                        </li>
                        <li>
                            <strong>Desktop processes:</strong> Run customer/product/order sync from the Run section as
                            needed, then run <strong>Group</strong> sync when you use QuickBooks group items so Fora can
                            build bill-of-material relationships correctly.
                        </li>
                        <li>
                            <strong>Logs:</strong> Use logs to verify desktop runs and resolve integration issues.
                        </li>
                    </ul>
                    <p className="text-sm text-gray-600 leading-relaxed mt-4">
                        <strong>QuickBooks Desktop</strong> is not the same as QuickBooks Online; pick this platform only
                        when your live data is in the desktop product.
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed mt-2">
                        If Fora already has manually created or imported customer/product data, align records with
                        QuickBooks-first sync before running order sync to avoid downstream matching issues.
                    </p>
                </section>

                <QuickBooksTrademarkSection />
                <ForaSignupSection />
                <LearnMoreFooter />
            </div>
        </LearnMoreShell>
    );
}
